import { useSelector } from 'react-redux';
import { Icon, Txt } from '@vst/beam';
import ShoppingCart from '@vst/beam-icons/icons/ShoppingCart';
import { useTranslation } from 'react-i18next';

import { Collapsible } from '@app/components/Collapsible';
import { useScreenResolution } from '@app/hooks';
import { formatPrice, formatPriceWithCurrency, selectChangePlan } from '@app/redux/changePlan';
import { selectLocale } from '@app/redux/locale';

import { GetPlanOffersPayload } from '@mfe/shared/schema-types';

import CartSummary from '../../components/CartSummary';

import styles from './styles.module.scss';

type CollapsibleCartSummaryProps = {
  selectedPlan: GetPlanOffersPayload;
  handleBackNavigation: (e: React.MouseEvent<HTMLElement>) => void;
};

const CollapsibleCartSummary = ({ handleBackNavigation, selectedPlan }: CollapsibleCartSummaryProps) => {
  const { t } = useTranslation('ChangePlan', {
    keyPrefix: 'orderReview.cartDetails.cartSummary',
  });

  const { isExtraSmall, isSmall } = useScreenResolution();
  const isMobile = isExtraSmall || isSmall;

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    cart: {
      prices: {
        monthly: { totalWithoutPromos },
        oneTime: { feesAndDiscounts },
      },
    },
    requiresEquipmentChange,
  } = useSelector(selectChangePlan);

  const { price, priceCurrency } = selectedPlan;

  const priceWithCurrency = formatPrice(userLocale, totalWithoutPromos ? totalWithoutPromos : price, priceCurrency);

  const pricingText = isMobile ? t('monthlyPricingMobile') : t('monthlyPricingDesktop');

  const oneTimeTotal = feesAndDiscounts?.reduce((sum, feeAndDiscount) => sum + (feeAndDiscount.total ?? 0), 0);

  const formattedOneTimeTotal = formatPriceWithCurrency(userLocale, oneTimeTotal, priceCurrency, 0);

  const shouldDisplayOneTimeFee = requiresEquipmentChange && feesAndDiscounts && feesAndDiscounts?.length > 0;

  return (
    <Collapsible
      classNames={{
        summary: styles['cart-accordion-summary'],
      }}
      summary={
        <div
          data-cy="collapsible-cart-summary"
          style={{
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            gap: '8px',
          }}
        >
          <Icon icon={ShoppingCart} color="gray_600" />
          <Txt variant="smallRegular" color="subtle" data-cy="collapsible-cart-summary-pricing-text">
            {pricingText}
            <Txt component="span" variant="smallBold" color="regular" data-cy="collapsible-cart-summary-price">
              {priceWithCurrency}
            </Txt>
          </Txt>

          {shouldDisplayOneTimeFee && (
            <Txt
              variant="smallRegular"
              color="subtle"
              data-cy="collapsible-cart-summary-one-time-fees"
              style={{ marginLeft: 'auto' }}
            >
              {isMobile ? t('oneTimeFeesMobile') : t('oneTimeFeesDesktop')}
              <Txt
                component="span"
                variant="smallBold"
                color="regular"
                data-cy="collapsible-cart-summary-one-time-fees-total"
              >
                {formattedOneTimeTotal}
              </Txt>
            </Txt>
          )}
        </div>
      }
      content={
        <CartSummary
          selectedPlan={selectedPlan}
          handleBackNavigation={handleBackNavigation}
          requiresEquipmentChange={requiresEquipmentChange}
        />
      }
    />
  );
};

export default CollapsibleCartSummary;
